import React, { useEffect } from "react";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { SingleValue } from "react-select";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "../../../api/useCategories";

export const useDegradationTypeInput = (selectedEntry: { degradationTypeId: string | null }) => {
  const degradationType = useCategories("DEGRADATION_TYPE");
  const createDegradationType = useCreateCategory("DEGRADATION_TYPE");
  const updateDegradationType = useUpdateCategory("DEGRADATION_TYPE");
  const deleteDegradationType = useDeleteCategory("DEGRADATION_TYPE");
  const [dirty, setDirty] = React.useState(false);
  const degradationTypeOptions = React.useMemo(
    () => buildOptions([...degradationType.map(e => ({ label: e, value: e }))]),
    [degradationType],
  );

  const degradationTypeDefaultValue = React.useMemo(
    () =>
      degradationType
        .filter(id => selectedEntry?.degradationTypeId === id)
        .map(id => ({ value: id, label: id }))[0] ?? null,
    [selectedEntry?.degradationTypeId, degradationType],
  );

  const [selectedDegradationType, setSelectedDegradationType] = React.useState<
    SingleValue<BaseOption>
  >(degradationTypeDefaultValue);

  useEffect(() => {
    if (
      selectedDegradationType?.value &&
      !degradationType.includes(selectedDegradationType.value)
    ) {
      setSelectedDegradationType(null);
    }
  }, [selectedDegradationType, degradationType]);

  const handleDegradationTypeChanged = React.useCallback(
    (value: SingleValue<BaseOption>) => {
      setSelectedDegradationType(value);
      setDirty(value !== degradationTypeDefaultValue);
    },
    [degradationTypeDefaultValue],
  );

  const handleDegradationTypeCreated = React.useCallback(
    async (newDegradationType: string) => {
      setSelectedDegradationType({ value: newDegradationType, label: newDegradationType });
      await createDegradationType(newDegradationType);
      setDirty(true);
    },
    [createDegradationType],
  );

  const handleDegradationTypeDeleted = React.useCallback(
    async (option: BaseOption) => {
      if (option.value) {
        if (selectedDegradationType?.value === option.value) setSelectedDegradationType(null);
        await deleteDegradationType(option.value);
        setDirty(true);
      }
    },
    [deleteDegradationType, selectedDegradationType?.value],
  );

  const handleDegradationTypeEdited = React.useCallback(
    async ({ value }: BaseOption, label: string) => {
      if (value) {
        setSelectedDegradationType({ label, value: label });
        await updateDegradationType(value, label);
        setDirty(true);
      }
    },
    [updateDegradationType],
  );

  const resetValues = React.useCallback(() => {
    setSelectedDegradationType(degradationTypeDefaultValue);
    setDirty(false);
  }, [degradationTypeDefaultValue]);

  return {
    degradationType,
    setSelectedDegradationType,
    degradationTypeOptions,
    degradationTypeDefaultValue,
    selectedDegradationType,
    dirty,
    resetValues,
    handleDegradationTypeChanged,
    handleDegradationTypeCreated,
    handleDegradationTypeDeleted,
    handleDegradationTypeEdited,
  };
};
